import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import HomelinkLoadingSvg from './HomelinkLoadingSvg';
import { useWindowDimensions } from 'hooks/useWindowDimensions';

interface LoadingDialogContextType {
  showLoading: (loadingMessage?: string) => void;
  hideLoading: () => void;
  isLoading: boolean;
}

const LoadingDialogContext = createContext<LoadingDialogContextType>({
  showLoading: () => {},
  hideLoading: () => {},
  isLoading: false,
});

export const useLoadingDialog = () => useContext(LoadingDialogContext);

// Global functions that can be called from anywhere
let globalShowLoading: ((loadingMessage?: string) => void) | null = null;
let globalHideLoading: (() => void) | null = null;

export const showLoadingDialog = (loadingMessage?: string) => {
  if (globalShowLoading) {
    globalShowLoading(loadingMessage);
  } else {
    console.warn('LoadingDialog context not initialized');
  }
};

export const hideLoadingDialog = () => {
  if (globalHideLoading) {
    globalHideLoading();
  } else {
    console.warn('LoadingDialog context not initialized');
  }
};

export const LoadingDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(
    undefined,
  );

  const showLoading = (message?: string) => {
    setLoadingMessage(message);
    setIsLoading(true);
  };

  const hideLoading = () => {
    setIsLoading(false);
    setLoadingMessage(undefined);
  };

  // Set global functions
  useEffect(() => {
    globalShowLoading = showLoading;
    globalHideLoading = hideLoading;
    return () => {
      globalShowLoading = null;
      globalHideLoading = null;
    };
  }, []);

  return (
    <LoadingDialogContext.Provider
      value={{ showLoading, hideLoading, isLoading }}
    >
      {children}
      <LoadingDialogComponent
        open={isLoading}
        loadingMessage={loadingMessage}
      />
    </LoadingDialogContext.Provider>
  );
};

interface LoadingDialogProps {
  open: boolean;
  loadingMessage?: string;
}

const LoadingDialogComponent = ({
  open,
  loadingMessage,
}: LoadingDialogProps) => {
  const { isMobile, infoDialogSize } = useWindowDimensions();
  return (
    <Dialog
      open={open}
      // fullScreen
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: isMobile ? '16px' : '24px',
          padding: 0,
          backgroundColor: 'white',
          width: infoDialogSize.width,
          height: (infoDialogSize.width / 4) * 3,
          // height: '100%',
          // width: '100%',
        }}
      >
        <HomelinkLoadingSvg
          width={isMobile ? 128 : 200}
          height={isMobile ? 93 : 146}
        />
        {loadingMessage && (
          <Typography
            className={`fontColor--green fontSize--${isMobile ? '14' : '16'} fontWeight--bold textAlignCenter m24t`}
          >
            {loadingMessage}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};
